import { useEffect, useState } from "react";

/**
 * Returns the user's browser timezone, safely handling SSR.
 *
 * Returns "UTC" during server-side rendering to ensure deterministic
 * hydration, then corrects to the real browser timezone on the client.
 *
 * Use this on public SSR pages where no venue/event timezone is available.
 * On dashboard pages, use venue.timezone directly instead — it's already
 * an explicit string and works the same on server and client.
 *
 * @returns IANA timezone string ("UTC" during SSR, then e.g. "America/Edmonton")
 *
 * @example
 * // Public page: derive dates from URL params in the user's timezone
 * const timezone = useBrowserTimezone();
 * const startDate = getStartOfDayInTimezone(timezone, query.start);
 *
 * @example
 * // Dashboard page: DON'T use this hook, use venue timezone directly
 * const today = getStartOfDayInTimezone(venue.timezone);
 */
export function useBrowserTimezone(): string {
  const [timezone, setTimezone] = useState("UTC");

  useEffect(() => {
    if (typeof Intl !== "undefined") {
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }, []);

  return timezone;
}
